import { defineStore } from 'pinia'
import { computed, inject, ref, shallowRef } from 'vue'
import {
  useDirtyState,
  useErreurDirtyState,
  useImmerFieldPatcher,
  useReglesFieldState,
  usePropertyStatusFieldState,
  useRemoteLogger,
} from '@/features/interface/composables'

import type { Patch } from 'immer'
import {
  type CheckedPropertyStatus,
  type PropertyStatusConfiguration,
  type Status,
  cleanNotPresent,
  restoreNotPresent,
} from '@/features/domaine/property-status'
import { mergeModelState } from '@/features/domaine/model-state-tools'
import { type ErreurWithDirty, type ErreurWithDirtyState } from '@/features/application/dirty'
import type { ModelState } from '@/features/domaine/types/techniques'
import { type ContextReglesDemande } from '@/features/domaine/regles-akto/shared'
import { loadExtaLov, demandeService, type IDemandeService } from '@/features/services/demande'
import { useAppStateStore } from '@/features/application'
import { TypesContextesAutorisesEnum, type TypeContextCourant } from '@/features/auth/context-app'
import {
  Dispositif,
  LevelRegleEnum,
  type NaturesFichierProvider,
  type NaturesFichiersDemande,
} from '@/features/domaine/types/opco'
import type { DemandeDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { Regles } from '@/features/domaine/moteur-regles'
import { validateNir, getRncpSilent } from '@/features/services/demande/DemandeAllService'
import { nextTick } from 'vue'

export type MetaDemande<TDemande extends DemandeDTO> = ModelState<
  TDemande,
  ErreurWithDirty<TDemande, number> & Status<TDemande>
>

export type BuildDemandeStoreOptions<
  TDemande extends DemandeDTO,
  TLov extends object,
  TContextReglesDemande extends ContextReglesDemande<TDemande, TLov>,
> = {
  dispositif: Dispositif
  demandeService: IDemandeService<TDemande, TLov>
  propertyStatusConfiguration: PropertyStatusConfiguration<TDemande, TContextReglesDemande>
  reglesConfiguration: Regles<TDemande, TContextReglesDemande, LevelRegleEnum>
}

export function buildDemandeStore<
  TDemande extends DemandeDTO,
  TLov extends object = object,
  TContextReglesDemande extends ContextReglesDemande<TDemande, TLov> = ContextReglesDemande<TDemande, TLov>,
>(options: BuildDemandeStoreOptions<TDemande, TLov, TContextReglesDemande>) {
  return defineStore(`storeDemande${options.dispositif}`, () => {
    const model = shallowRef<TDemande>()
    const lov = shallowRef<TLov>(null as any)
    const meta = shallowRef<MetaDemande<TDemande>>(null as any)
    const dirtyStateManager = useDirtyState<TDemande>()
    const erreurStateManager = useReglesFieldState(options.reglesConfiguration)
    const erreurDirtyStateManager = useErreurDirtyState<TDemande>()
    const propertyStatusStateManager = usePropertyStatusFieldState<TDemande, TContextReglesDemande>(
      options.propertyStatusConfiguration,
    )
    const appState = useAppStateStore()
    const estModifiéInt = ref<boolean>(false)
    const estModifié = computed(() => estModifiéInt.value)
    const remotelogger = useRemoteLogger()

    const $reset = () => {
      model.value = null as any
      meta.value = null as any
      estModifiéInt.value = false
      erreurDirtyStateManager.$reset()
      dirtyStateClear()
    }

    async function preserveEstModifié(action: () => void | Promise<void>) {
      const estModifiéBefore = estModifiéInt.value
      await action()
      if (!estModifiéBefore) {
        nextTick(() => {
          if (estModifiéInt.value) {
            estModifiéInt.value = false
          }
        })
      }
    }

    const readonly = computed<boolean>(() => {
      if (!model.value) return true
      if (appState.getPermissions(options.dispositif) !== 'Ecriture') return true
      if (appState.contextCourant === TypesContextesAutorisesEnum.Adherent) {
        return ['RepriseAdherent', 'SaisieAdherent'].includes(model.value.statut) === false
      } else if (appState.contextCourant === TypesContextesAutorisesEnum.Prestataire) {
        return model.value.statut !== 'SaisiePrestataire'
      }
      return true
    })

    const mustConfirm = computed<boolean>(() => {
      if (!model.value) return false
      if (appState.getPermissions(options.dispositif) !== 'Ecriture') return false
      if (appState.contextCourant === TypesContextesAutorisesEnum.Adherent) {
        return ['AReprendreAdherent'].includes(model.value.statut)
      }
      return false
    })

    const canRefuse = computed<boolean>(() => {
      if (!model.value) return false
      if (appState.getPermissions(options.dispositif) !== 'Ecriture') return false
      if (appState.contextCourant === TypesContextesAutorisesEnum.Adherent) {
        return model.value.statut === 'RepriseAdherent'
      }
      return false
    })

    const typeContextCourant = computed<TypeContextCourant>(() => appState.contextCourant)

    const documentsProvider = inject<NaturesFichierProvider>(`NATURE_${options.dispositif}`)!
    const naturesFichiers = computed<NaturesFichiersDemande>(() => {
      return documentsProvider.getNaturesFichiers(model.value!, typeContextCourant.value)
    })

    function getModelContext() {
      return {
        lov: lov.value,
        status: propertyStatusStateManager.state.value,
        typeContext: typeContextCourant.value,
        naturesFichiers: naturesFichiers.value,
        services: {
          validerNir: validateNir,
          getRncp: getRncpSilent,
        },
      } as TContextReglesDemande
    }

    async function loadData(id: number) {
      $reset()

      // Charge la Demande
      model.value = await options.demandeService.loadDemande(id, { dialogExcludeErrors: [403] })

      // Charge les LOV
      const extraLov = await loadExtaLov()
      lov.value = { ...(await options.demandeService.loadLov()), ...extraLov }

      // Calcul des StatusProperty (champs obligoires)
      propertyStatusStateManager.checkStatus(model.value, getModelContext())

      // Calcule les erreurs sur le modele
      await erreurStateManager.executeAsync(model.value, getModelContext())

      dirtifyErrorLevel(LevelRegleEnum.Metier)

      // Fusionne Erreur et Dirty
      erreurDirtyStateManager.dirtify(erreurStateManager.state.value, dirtyStateManager.state.value)

      // Fusionne ErreurDirty et StatusProperty
      fusionneErreurDirtyEtPropertyStatut()
    }

    function dirtifyReject(level: LevelRegleEnum) {
      dirtifyErrorLevel(level)

      // Fusionne Erreur et Dirty
      erreurDirtyStateManager.dirtify(erreurStateManager.state.value, dirtyStateManager.state.value)

      // Fusionne ErreurDirty et StatusProperty
      fusionneErreurDirtyEtPropertyStatut()
    }

    async function saveData(): Promise<boolean> {
      if (meta.value.$estErreur && meta.value.$level === LevelRegleEnum.Technique) return false
      try {
        // Purger les jamais renseignés (PropertyStatus.NotPresent)
        const [cleanModel, notPresentModel] = cleanNotPresent(model.value!, propertyStatusStateManager.state.value)

        // Sauvegarder les données cleannées
        const resultModel = await options.demandeService.saveDemande(cleanModel)

        // Restore les les jamais renseignés (PropertyStatus.NotPresent)
        model.value = restoreNotPresent(resultModel, notPresentModel)

        // Calcule les erreurs sur le modele
        await erreurStateManager.executeAsync(model.value, getModelContext())

        // Les dirty n'ont pas changé
        // Ne rien faire dessus

        // Fusionne ErreurDirty et StatusProperty
        fusionneErreurDirtyEtPropertyStatut()

        estModifiéInt.value = false

        return true
      } catch (err) {
        // Ne rien faire
        remotelogger.error(err as Error, model.value, meta.value)
        return false
      }
    }

    async function submitData(isAdherentOuMandatGestion: boolean): Promise<boolean> {
      // Todo : Mettre le Level
      if (meta.value.$estErreur && meta.value.$level === LevelRegleEnum.Technique) return false

      try {
        // Purger les jamais renseignés (PropertyStatus.NotPresent)
        const [cleanModel] = cleanNotPresent(model.value!, propertyStatusStateManager.state.value)

        // Sauvegarder les données
        if (isAdherentOuMandatGestion === true) await demandeService.submitDemande('EnCoursDeTransmission', cleanModel)
        else await demandeService.submitDemande('AReprendreAdherent', cleanModel)

        // Recharge la demande
        model.value = await options.demandeService.loadDemande(model.value!.id)

        // Calcule les erreurs sur le modele
        await erreurStateManager.executeAsync(model.value, getModelContext())

        // Elimine les Dirty
        // Normalement il n'y en a pas
        dirtyStateClear()
        fusionneErreurDirtyEtPropertyStatut()

        estModifiéInt.value = false

        return true
      } catch (err) {
        remotelogger.error(err as Error, model.value, meta.value)

        // Ne rien faire
        return false
      }
    }

    async function confirmData(accept: boolean): Promise<boolean> {
      try {
        if (appState.contextCourant === TypesContextesAutorisesEnum.Adherent) {
          // Purger les jamais renseignés (PropertyStatus.NotPresent)
          const [cleanModel] = cleanNotPresent(model.value!, propertyStatusStateManager.state.value)

          if (accept === true) {
            await demandeService.submitDemande('RepriseAdherent', cleanModel)

            // Recharge la demande
            model.value = await options.demandeService.loadDemande(model.value!.id)

            // Calcule les erreurs sur le modele
            await erreurStateManager.executeAsync(model.value, getModelContext())

            fusionneErreurDirtyEtPropertyStatut()

            estModifiéInt.value = false
          } else {
            await demandeService.submitDemande('RefusAdherent', cleanModel)
          }
        }

        return true
      } catch (err) {
        remotelogger.error(err as Error, model.value, meta.value)

        // Ne rien faire
        return false
      }
    }

    async function downloadCerfa(): Promise<Blob> {
      return await options.demandeService.downloadCerfa(model.value!.id!)
    }

    function fusionneErreurDirtyEtPropertyStatut() {
      // Fusionne ErreurDirty et StatusProperty
      meta.value = mergeModelState<TDemande, ErreurWithDirtyState<number>, CheckedPropertyStatus>(
        erreurDirtyStateManager.state.value,
        propertyStatusStateManager.state.value,
        {
          defaultA: {
            $estDirty: false,
          },
        },
      )
    }

    async function appliquerPatch(newModel: TDemande, newPatchs: Patch[]) {
      // Applique les modifications du modele
      model.value = newModel

      // Calcul des StatusProperty (champs obligoires)
      propertyStatusStateManager.checkStatus(newModel, getModelContext())

      // Flag le modele comme modifié
      estModifiéInt.value = true

      // Calcule les erreurs sur le modele
      if (await erreurStateManager.executeAsync(newModel, getModelContext()) === false) return

      // Calcule les dirty sur le modele avec les patchs
      dirtyStateManager.applyPatch(newPatchs)

      // Fusionne Erreur et Dirty
      erreurDirtyStateManager.dirtify(erreurStateManager.state.value, dirtyStateManager.state.value)

      // Fusionne ErreurDirty et StatusProperty
      fusionneErreurDirtyEtPropertyStatut()
    }

    function dirtyStateClear() {
      dirtyStateManager.$reset()
      erreurDirtyStateManager.$reset()
    }

    function dirtifyErrorLevel(level: LevelRegleEnum) {
      // Calcule les dirty sur les meta avec le callback
      dirtyStateManager.pathDirtyWithCallback(meta.value, erreurStateManager.state.value, (state) => {
        if (state.$estErreur !== true) return state
        if (state.$estDirty === true) return state
        if (state.$level! > level) return state

        return { $estDirty: true }
      })
    }

    const { setupFieldWithKey, setupFieldWithOptions, setupArrayUpdaterWithKey, setupCustomUpdater } =
      useImmerFieldPatcher<TDemande>({
        getModel: () => model.value!,
        setModel: (newModel, patchs) => appliquerPatch(newModel, patchs),
      })

    return {
      model,
      meta,
      lov,
      readonly,
      estModifié,
      preserveEstModifié,
      mustConfirm,
      canRefuse,
      loadData,
      saveData,
      submitData,
      confirmData,
      downloadCerfa,
      dirtifyReject,
      setupFieldWithKey,
      setupFieldWithOptions,
      setupArrayUpdaterWithKey,
      setupCustomUpdater,
      typeContextCourant,
      naturesFichiers,
    }
  })
}
